import React, { useState, useContext, useEffect } from "react";
import { getSubWords, randomizeLetters } from "../utilities";

import allWords from "../assets/words.json";
import { getLevelForGamesWon } from "../config";

export interface IWord {
  word: string;
  found: boolean;
  hint: { [key: number]: boolean };
}

interface IAnagramContext {
  letters: string[];
  anagrams: IWord[];
  foundWords: string[];
  bonusWords: string[];
  hintCount: number;
  winner: boolean;
  level: number;
  gamesWon: number;
  getNewAnagrams: () => void;
  checkWord: (word: string) => void;
  resetWinner: () => void;
  getAClue: () => void;
  shuffleTheLetters: () => void;
}

const defaultState: IAnagramContext = {
  letters: [],
  anagrams: [],
  foundWords: [],
  bonusWords: [],
  hintCount: 2,
  winner: false,
  level: 0,
  gamesWon: 0,
  getNewAnagrams: () => {
    console.log("Not actually generating new anagrams.");
  },
  checkWord: (word: string) => {
    console.log("Not actually checking the word.");
  },
  resetWinner: () => {
    console.log("Reset winner not actually resetting the winner");
  },
  getAClue: () => {
    console.log("Not actually getting a clue");
  },
  shuffleTheLetters: () => {
    console.log("Not actually shuffling the letters");
  },
};

const AnagramsContext = React.createContext<IAnagramContext>(defaultState);

function readStoredGamesWon(): number {
  const stored = localStorage.getItem("gamesWon");
  return stored ? parseInt(stored) : 0;
}

// Builds a fresh word set for the level that matches `gamesWon`.
// The anagram is always the last entry.
function generateWordSet(gamesWon: number): IWord[] {
  const { words, maxWordLength } = getLevelForGamesWon(gamesWon);
  return getSubWords(words, maxWordLength);
}

function lastWordOf(words: IWord[]): string {
  return words[words.length - 1].word;
}

export function AnagramsProvider(props: { children: any }) {
  const [gamesWon, setGamesWon] = useState<number>(readStoredGamesWon);
  const [anagrams, setAnagrams] = useState<IWord[]>(() =>
    generateWordSet(gamesWon)
  );
  const [letters, setLetters] = useState<string[]>(() =>
    randomizeLetters(lastWordOf(anagrams))
  );
  const [bonusWords, setBonusWords] = useState<string[]>([]);
  const [hintCount, setHintCount] = useState<number>(2);
  const [winner, setWinner] = useState<boolean>(false);
  const [foundWords, setFoundWords] = useState<string[]>([]);

  // level is derived from gamesWon so it can never disagree with the words
  const level = getLevelForGamesWon(gamesWon).level;

  useEffect(() => {
    localStorage.setItem("gamesWon", `${gamesWon}`);
  }, [gamesWon]);

  function getNewAnagrams() {
    const newWords = generateWordSet(gamesWon);

    setFoundWords([]);
    setAnagrams(newWords);
    setLetters(randomizeLetters(lastWordOf(newWords)));
  }

  function markWordFound(word: string) {
    const updatedAnagrams = anagrams.map((anagram) =>
      anagram.word === word ? { ...anagram, found: true } : anagram
    );

    setFoundWords([...foundWords, word]);
    setAnagrams(updatedAnagrams);

    if (updatedAnagrams.every((anagram) => anagram.found)) {
      setWinner(true);
      setGamesWon(gamesWon + 1);
      if (gamesWon % 3 === 0) {
        setHintCount(hintCount + 1);
      }
    }
  }

  function checkWord(word: string) {
    if (anagrams.some((anagram) => anagram.word === word)) {
      // a target word only scores once; re-entering it earns nothing
      if (!foundWords.includes(word)) {
        markWordFound(word);
      }
      return;
    }

    if (!bonusWords.includes(word) && allWords.words.includes(word)) {
      setBonusWords([...bonusWords, word]);
    }
  }

  function getAClue() {
    if (hintCount <= 0) {
      return;
    }

    // reveal a letter of the first word that hasn't been found yet
    const unfoundWord = anagrams.find(
      (anagram) => !foundWords.includes(anagram.word)
    );
    if (!unfoundWord) {
      return;
    }

    const hintIdx = unfoundWord.word
      .split("")
      .findIndex((_letter, idx) => unfoundWord.hint[idx] !== true);
    if (hintIdx === -1) {
      return;
    }

    setAnagrams(
      anagrams.map((anagram) =>
        anagram.word === unfoundWord.word
          ? { ...anagram, hint: { ...anagram.hint, [hintIdx]: true } }
          : anagram
      )
    );
    setHintCount(hintCount - 1);
  }

  function resetGame() {
    getNewAnagrams();
    setBonusWords([]);
    setWinner(false);
  }

  function shuffleTheLetters() {
    setLetters(randomizeLetters(letters.join("")));
  }

  return (
    <AnagramsContext.Provider
      value={{
        letters,
        anagrams,
        foundWords,
        bonusWords,
        hintCount,
        level,
        gamesWon,
        winner,
        getNewAnagrams,
        checkWord,
        resetWinner: resetGame,
        getAClue,
        shuffleTheLetters,
      }}
    >
      {props.children}
    </AnagramsContext.Provider>
  );
}

export const useAnagramsContext = () => useContext(AnagramsContext);
